/* SectionLiveDataPanel — collapsible "📡 Live Data Signals" panel that sits
 * next to the SectionSourcesPanel on each generated blueprint section.
 * Shows the exact live-API numbers Claude was anchored on when writing this
 * section, with freshness timestamps and per-provider status dots.
 */
import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { Radio, ChevronDown, ChevronRight, ExternalLink, Loader2, CheckCircle2, AlertTriangle, TrendingUp, TrendingDown, Minus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROVIDER_LABELS = {
  fx: 'FX (ECB)',
  gdelt: 'GDELT News',
  wikipedia: 'Wiki Pageviews',
  worldbank: 'World Bank',
  imf: 'IMF WEO',
  undp: 'UNDP',
  oecd: 'OECD',
  wb_governance: 'WB Governance',
  wb_findex: 'WB Findex',
  who: 'WHO GHO',
  nasa_power: 'NASA POWER',
  comtrade: 'UN Comtrade',
  rest_countries: 'REST Countries',
  ti_cpi: 'TI CPI 2024',
  vdem: 'V-Dem',
  freedom_house: 'Freedom House',
};

const FRESHNESS_PILL = {
  FRESH:   { bg: 'bg-emerald-500/15', border: 'border-emerald-500/30', text: 'text-emerald-700 dark:text-emerald-300', dot: 'bg-emerald-500',     label: 'FRESH' },
  STALE:   { bg: 'bg-amber-500/15',   border: 'border-amber-500/30',   text: 'text-amber-700 dark:text-amber-300',     dot: 'bg-amber-500',       label: 'STALE' },
  FATAL:   { bg: 'bg-rose-500/15',    border: 'border-rose-500/30',   text: 'text-rose-700 dark:text-rose-300',        dot: 'bg-rose-500',        label: 'OUTDATED' },
  UNKNOWN: { bg: 'bg-slate-500/15',   border: 'border-slate-500/25', text: 'text-slate-600 dark:text-slate-400',       dot: 'bg-slate-400',       label: '—' },
};

export default function SectionLiveDataPanel({ countryId, sectionKey, darkMode = false }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const fetchData = useCallback(async () => {
    if (data || loading) return;
    setLoading(true); setErr(null);
    try {
      const r = await axios.get(`${API}/countries/${countryId}/sections/${sectionKey}/live-data`, { timeout: 30000 });
      setData(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to fetch live data');
    } finally { setLoading(false); }
  }, [countryId, sectionKey, data, loading]);

  const toggle = () => {
    setOpen(prev => { const n = !prev; if (n) fetchData(); return n; });
  };

  const tint = darkMode
    ? { bg: 'bg-white/5', border: 'border-white/10', hover: 'hover:bg-white/10', text: 'text-white', muted: 'text-white/50', cardBg: 'bg-white/5', cardBorder: 'border-white/10' }
    : { bg: 'bg-[#ecfdf5]', border: 'border-[#10b981]/30', hover: 'hover:bg-[#d1fae5]', text: 'text-[#064e3b]', muted: 'text-[#6b7280]', cardBg: 'bg-white', cardBorder: 'border-[#10b981]/20' };

  const fresh = (data?.signals || []).filter(s => s.freshness_days !== null && s.freshness_days <= 45);
  const baseline = (data?.signals || []).filter(s => !(s.freshness_days !== null && s.freshness_days <= 45));

  return (
    <div className={`mt-3 rounded-xl border ${tint.border} ${tint.bg} overflow-hidden`} data-testid={`livedata-panel-${sectionKey}`}>
      <button onClick={toggle} data-testid={`livedata-toggle-${sectionKey}`}
        className={`w-full flex items-center justify-between gap-3 px-4 py-3 text-left ${tint.hover} transition-all`}>
        <div className="flex items-center gap-2.5">
          <div className="relative">
            <Radio className={`w-4 h-4 ${darkMode ? 'text-[#6ee7b7]' : 'text-[#10b981]'}`} />
            <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-[#10b981] animate-pulse" />
          </div>
          <div>
            <p className={`text-xs font-bold uppercase tracking-widest ${tint.text}`}>Live Data Signals <span className={`ml-1 text-[9px] font-black px-1.5 py-0.5 rounded-full ${darkMode ? 'bg-[#10b981]/20 text-[#6ee7b7]' : 'bg-[#10b981]/15 text-[#047857]'}`}>BETA</span></p>
            <p className={`text-[10.5px] ${tint.muted} leading-tight`}>
              {data ? `${data.signals.length} signals from ${data.providers_ok?.length || 0} APIs · ${data.fresh_count} fresh (≤45 days old)` : 'Tap to fetch real-time API data anchoring this section'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {data && (
            <>
              <span className={`text-[10px] font-black px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 border border-emerald-500/30`} title="FRESH signals (within SLA)">
                {data.freshness_buckets?.FRESH ?? data.fresh_count} FRESH
              </span>
              {(data.freshness_buckets?.STALE ?? 0) > 0 && (
                <span className={`text-[10px] font-black px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-700 border border-amber-500/30`} title="STALE signals (past fresh SLA, within fatal)">
                  {data.freshness_buckets.STALE} STALE
                </span>
              )}
              {(data.freshness_buckets?.FATAL ?? 0) > 0 && (
                <span className={`text-[10px] font-black px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-700 border border-rose-500/30`} title="Signals past fatal SLA — consider refresh">
                  {data.freshness_buckets.FATAL} OUTDATED
                </span>
              )}
            </>
          )}
          {open ? <ChevronDown className={`w-4 h-4 ${tint.muted}`} /> : <ChevronRight className={`w-4 h-4 ${tint.muted}`} />}
        </div>
      </button>

      {open && (
        <div className={`px-4 pb-4 pt-2 border-t ${tint.border}`} data-testid={`livedata-body-${sectionKey}`}>
          {loading && (
            <div className="flex items-center gap-2 py-4">
              <Loader2 className={`w-4 h-4 animate-spin ${tint.muted}`} />
              <span className={`text-xs ${tint.muted}`}>Fetching live API signals…</span>
            </div>
          )}
          {err && <p className="text-xs text-red-500 py-3">Error: {err}</p>}
          {data && (
            <>
              {/* Provider status dots */}
              <div className="flex flex-wrap items-center gap-1.5 mb-3 mt-2">
                <span className={`text-[10px] font-bold uppercase tracking-widest ${tint.muted} mr-1`}>APIs polled:</span>
                {['fx','gdelt','wikipedia','worldbank','imf','undp','oecd','wb_governance','wb_findex','who','nasa_power','comtrade','rest_countries','ti_cpi','vdem','freedom_house'].map(pid => {
                  const ok = data.providers_ok?.includes(pid);
                  const failed = data.providers_failed?.includes(pid);
                  const notCalled = !ok && !failed;
                  return (
                    <span key={pid} className={`text-[9.5px] px-1.5 py-0.5 rounded border flex items-center gap-1 ${ok ? (darkMode ? 'border-emerald-400/40 text-emerald-300 bg-emerald-500/10' : 'border-emerald-300 text-emerald-700 bg-emerald-50') : failed ? (darkMode ? 'border-red-400/40 text-red-300 bg-red-500/10' : 'border-red-300 text-red-600 bg-red-50') : (darkMode ? 'border-white/10 text-white/30' : 'border-slate-200 text-slate-400')}`} data-testid={`provider-status-${pid}`}>
                      {ok && <CheckCircle2 className="w-2.5 h-2.5" />}
                      {failed && <AlertTriangle className="w-2.5 h-2.5" />}
                      {notCalled && <Minus className="w-2.5 h-2.5" />}
                      {PROVIDER_LABELS[pid] || pid}
                    </span>
                  );
                })}
              </div>
              <p className={`text-[11px] ${tint.muted} mb-3 leading-relaxed`}>
                Same live signals injected into Claude's prompt when this section was written. Claude was instructed to cite these exact numbers inline.
              </p>

              {/* Fresh block */}
              {fresh.length > 0 && (
                <>
                  <p className={`text-[10px] font-black uppercase tracking-[0.18em] ${darkMode ? 'text-emerald-300' : 'text-emerald-700'} mb-2 flex items-center gap-1.5`}>
                    <Radio className="w-3 h-3" /> Fresh signals · ≤45 days
                  </p>
                  <div className="grid md:grid-cols-2 gap-1.5 mb-3">
                    {fresh.map((s, i) => <SignalCard key={`f-${i}`} s={s} tint={tint} />)}
                  </div>
                </>
              )}

              {/* Baseline block */}
              {baseline.length > 0 && (
                <>
                  <p className={`text-[10px] font-black uppercase tracking-[0.18em] ${tint.muted} mb-2`}>
                    Baseline · authoritative anchors
                  </p>
                  <div className="grid md:grid-cols-2 gap-1.5">
                    {baseline.map((s, i) => <SignalCard key={`b-${i}`} s={s} tint={tint} />)}
                  </div>
                </>
              )}

              {/* Failed providers footnote */}
              {data.providers_failed?.length > 0 && (
                <p className={`text-[10px] ${tint.muted} mt-3 italic`}>
                  Note: {data.providers_failed.join(', ')} temporarily unavailable or unsupported for this country — cached results from other providers still power citations.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function SignalCard({ s, tint }) {
  const val = s.value;
  const valStr = typeof val === 'number'
    ? (Math.abs(val) >= 1000 ? val.toLocaleString() : val.toFixed(Math.abs(val) < 10 ? 2 : 1))
    : String(val);
  const hasTrend = typeof s.trend_30d_pct === 'number';
  const TrendIcon = !hasTrend ? null : s.trend_30d_pct > 0 ? TrendingUp : s.trend_30d_pct < 0 ? TrendingDown : Minus;
  const trendColor = !hasTrend ? '' : s.trend_30d_pct > 0 ? 'text-red-500' : s.trend_30d_pct < 0 ? 'text-emerald-500' : 'text-slate-400';
  return (
    <div className={`p-2.5 rounded-lg ${tint.cardBg} border ${tint.cardBorder}`} data-testid={`signal-${s.provider}-${s.indicator}`}>
      <p className={`text-[10.5px] font-semibold leading-tight ${tint.text}`}>{s.label}</p>
      <div className="flex items-baseline gap-2 mt-1">
        <span className={`text-[14px] font-black ${tint.text}`}>{valStr}</span>
        {s.unit && s.unit !== 'status' && <span className={`text-[10px] ${tint.muted}`}>{s.unit}</span>}
        {hasTrend && (
          <span className={`text-[10px] font-bold flex items-center gap-0.5 ${trendColor}`}>
            <TrendIcon className="w-2.5 h-2.5" />
            {s.trend_30d_pct > 0 ? '+' : ''}{s.trend_30d_pct}%
          </span>
        )}
      </div>
      <div className="flex items-center justify-between mt-1 gap-1">
        <span className={`text-[9px] ${tint.muted}`}>{s.provider} · {s.as_of}</span>
        <div className="flex items-center gap-1 shrink-0">
          {s.freshness_status && s.freshness_status !== 'UNKNOWN' && (
            <span className={`text-[8.5px] font-black px-1.5 py-0.5 rounded-full border flex items-center gap-1 ${FRESHNESS_PILL[s.freshness_status].bg} ${FRESHNESS_PILL[s.freshness_status].border} ${FRESHNESS_PILL[s.freshness_status].text}`}
                  title={`${s.freshness_status} · ${s.age_days ?? '?'} days old`}>
              <span className={`w-1 h-1 rounded-full ${FRESHNESS_PILL[s.freshness_status].dot}`} />
              {FRESHNESS_PILL[s.freshness_status].label}
            </span>
          )}
          {s.url && (
            <a href={s.url} target="_blank" rel="noopener noreferrer"
              className={`text-[9px] ${tint.muted} hover:text-[#10b981] flex items-center gap-0.5 shrink-0`}>
              source <ExternalLink className="w-2.5 h-2.5" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
